/**
 * Navigation Configuration
 *
 * Defines the header menus. All labels are hardcoded German strings.
 *
 * Structure: the consultant's final sheet (2026-10-21; Maßnahmen, Leistungen,
 * Für Vorhabenträger, Für Flächeneigentümer, Wissen) — see
 * knowledgebase/briefs/BRIEF-header-nav.md. Every href here is checked by
 * links.test.ts: it must reach a registered, published page or an app route,
 * and each of the first four menus must list exactly the pages its hub's card
 * grid shows (lib/site-structure), so menu and hub cannot drift apart.
 */

import type { LucideIcon } from "lucide-react";
import { routes } from "@/lib/routes";

// ============================================================================
// Type Definitions
// ============================================================================

export interface MegaMenuItem {
  id: string;
  label: string;
  href: string;
  description?: string;
}

export interface MegaMenuFeaturedImage {
  src: string;
  alt: string;
  href?: string;
}

export interface MegaMenuSection {
  id: string;
  title?: string;
  /** Makes the title a link — for entries that are a page and also group sub-pages. */
  href?: string;
  /** One line under a linked title (desktop only, like item descriptions). */
  description?: string;
  titleIcon?: LucideIcon;
  /** Items with a description render as title + line; without one, as plain links (Wissen). */
  items?: MegaMenuItem[];
  /** A row of small pill links under the items, e.g. the Branchen of "Für Vorhabenträger". */
  chips?: { title: string; links: { label: string; href: string }[] };
  columns?: 1 | 2;
}

export interface MegaMenuCTA {
  label: string;
  description?: string;
  href: string;
  variant: "primary" | "outline";
}

/** Featured card shown in the mega-menu sidebar (e.g., ÖPaaS teaser) */
export interface MegaMenuFeaturedCard {
  /** Small label above the headline; omit it where it would repeat the menu's own label. */
  badge?: string;
  headline: string;
  subtitle: string;
  ctaLabel: string;
  ctaHref: string;
}

export interface MegaMenuConfig {
  id: string;
  triggerLabel: string;
  sections: MegaMenuSection[];
  cta?: MegaMenuCTA;
  /**
   * Bottom strip: the hub link (with a grid icon), then further links (with an
   * arrow). Its first link is also where the menu's label leads.
   */
  bottomStrip: {
    primary: { label: string; href: string };
    links?: { label: string; href: string }[];
  };
  /** Layout variant */
  layout?: "default" | "featured-image" | "dropdown";
  /** Featured image for the panel (used with layout: "featured-image") */
  featuredImage?: MegaMenuFeaturedImage;
  /** Featured card for sidebar (e.g., ÖPaaS managed service teaser) */
  featuredCard?: MegaMenuFeaturedCard;
}

// ============================================================================
// Navigation Configuration
// ============================================================================

export const NAVIGATION_CONFIG: MegaMenuConfig[] = [
  // -------------------------------------------------------------------------
  // 1. Maßnahmen — what can be compensated (shelf, three columns).
  // -------------------------------------------------------------------------
  {
    id: "massnahmen",
    triggerLabel: "Maßnahmen",
    sections: [
      {
        id: "oekopunkte",
        title: "Ökopunkte",
        href: routes.content.oekopunkte,
        description: "Vorab umgesetzte Maßnahmen aus dem Ökokonto",
      },
      {
        id: "ausgleichsflaechen",
        title: "Ausgleichsflächen",
        href: routes.content.ausgleichsflaechen,
        items: [
          {
            id: "biotopausgleich",
            label: "Biotopausgleich",
            description: "Biotope aufwerten – auch Knicks und Wallhecken",
            href: routes.content.biotopausgleich,
          },
          {
            id: "ackerland-stilllegung",
            label: "Ackerland & Stilllegung",
            description: "Maßnahmen auf landwirtschaftlichen Flächen",
            href: routes.content.ackerlandStilllegung,
          },
          {
            id: "forstausgleich",
            label: "Forstausgleich & Waldumwandlung",
            description: "Ersatzaufforstung nach einer Waldumwandlung",
            href: routes.content.forstausgleichWaldumwandlung,
          },
          {
            id: "landschaftsbild",
            label: "Landschaftsbild & Erholung",
            description: "Ausgleich für Eingriffe ins Landschaftsbild",
            href: routes.content.landschaftsbildErholung,
          },
        ],
      },
      {
        id: "artenschutz",
        title: "Artenschutz",
        href: routes.content.artenschutz,
        items: [
          {
            id: "cef",
            label: "CEF-Maßnahmen",
            description: "Vorgezogener Ausgleich, z. B. für Feldlerche oder Haselmaus",
            href: routes.content.cefMassnahmen,
          },
          {
            id: "fcs",
            label: "FCS-Maßnahmen",
            description: "Erhaltungszustand sichern, wenn eine Ausnahme nötig ist",
            href: routes.content.fcsMassnahmen,
          },
        ],
      },
    ],
    bottomStrip: {
      primary: { label: "Alle Maßnahmen", href: routes.content.kompensationsmassnahmen },
      links: [
        { label: "Alle Kompensationsarten im Überblick", href: routes.content.compensationTypes },
        {
          label: "Was sind Kompensationsmaßnahmen?",
          href: routes.content.wasSindKompensationsmassnahmen,
        },
      ],
    },
  },

  // -------------------------------------------------------------------------
  // 2. Leistungen — what we do (small dropdown). The last three are delivered
  // by a partner; the pages say so, the menu does not need to.
  // -------------------------------------------------------------------------
  {
    id: "leistungen",
    triggerLabel: "Leistungen",
    layout: "dropdown",
    sections: [
      {
        id: "leistungen-liste",
        items: [
          {
            id: "oekopunkte-vermittlung",
            label: "Ökopunkte-Vermittlung",
            description: "Bedarf und Angebote für Ökopunkte zusammenbringen",
            href: routes.content.oekopunkteVermittlung,
          },
          {
            id: "flaechenvermittlung",
            label: "Flächenvermittlung",
            description: "Ausgleichsflächen zwischen Eigentümern und Vorhabenträgern vermitteln",
            href: routes.content.flaechenvermittlung,
          },
          {
            id: "rechtliche-absicherung",
            label: "Rechtliche Absicherung & Verträge",
            description: "Rund um Vertrag und dauerhafte Sicherung der Maßnahme",
            href: routes.content.rechtlicheAbsicherung,
          },
          {
            id: "behoerdenabstimmung",
            label: "Behördenabstimmung",
            description: "Abstimmung mit der Naturschutzbehörde vorbereiten",
            href: routes.content.behoerdenabstimmung,
          },
          {
            id: "oekologische-baubegleitung",
            label: "Ökologische Baubegleitung",
            description: "Naturschutzauflagen während der Bauphase begleiten",
            href: routes.content.oekologischeBaubegleitung,
          },
          {
            id: "lbp-beratung",
            label: "Landschaftspflegerischer Begleitplan",
            description: "Beratung zum LBP für Ihr Vorhaben",
            href: routes.content.landschaftspflegerischerBegleitplan,
          },
        ],
      },
    ],
    bottomStrip: {
      primary: { label: "Alle Leistungen", href: routes.content.leistungen },
    },
  },

  // -------------------------------------------------------------------------
  // 3. Für Vorhabenträger — buyers (shelf: two columns + Kompensationsberatung).
  // "Kompensation anfragen", not "beauftragen": in the ÖPaaS org area
  // "Beauftragen" is the binding §4 Abs. 3 commission.
  // -------------------------------------------------------------------------
  {
    id: "vorhabentraeger",
    triggerLabel: "Für Vorhabenträger",
    sections: [
      {
        id: "vorhaben",
        title: "Ihr Vorhaben",
        items: [
          {
            id: "oekopunkte-kaufen",
            label: "Ökopunkte kaufen",
            description: "Angebote und Preise je Bundesland",
            href: routes.content.oekopunkteKaufen,
          },
          {
            id: "kompensation-anfragen",
            label: "Kompensation anfragen",
            description: "Bedarf in der Schnellanfrage beschreiben",
            href: routes.content.kompensationAnfragen,
          },
          {
            id: "oekologische-baubegleitung",
            label: "Ökologische Baubegleitung",
            description: "Naturschutzauflagen während der Bauphase",
            href: routes.content.oekologischeBaubegleitung,
          },
        ],
        // The same four as the chips on the Vorhabenträger hub (vorhabentraeger.mdx).
        chips: {
          title: "Nach Branche",
          links: [
            { label: "Projektentwicklung", href: routes.content.projektentwickler },
            { label: "Infrastruktur", href: routes.content.infrastrukturentwickler },
            { label: "Kommunen", href: routes.content.kommune },
            { label: "Netzbetreiber", href: routes.content.netzbetreiber },
          ],
        },
      },
      {
        id: "erneuerbare-energien",
        title: "Erneuerbare Energien",
        href: routes.content.erneuerbareEnergien,
        items: [
          {
            id: "windenergie",
            label: "Windenergie-Projekte",
            description: "Eingriff, Artenschutz und Landschaftsbild",
            href: routes.content.kompensationWindenergie,
          },
          {
            id: "pv",
            label: "PV-Projekte",
            description: "Freiflächenanlagen im Bebauungsplan und im Außenbereich",
            href: routes.content.kompensationPhotovoltaik,
          },
          {
            id: "batteriespeicher",
            label: "Batteriespeicher-Projekte",
            description: "Großspeicher im Außenbereich",
            href: routes.content.kompensationBatteriespeicher,
          },
        ],
      },
    ],
    featuredCard: {
      badge: "Kompensationsberatung",
      headline: "Kompensation im Full Service",
      subtitle:
        "Wir übernehmen Ihr Kompensationsmanagement. Für Unternehmen mit wiederkehrendem Bedarf.",
      ctaLabel: "Beratung anfordern",
      ctaHref: routes.content.kompensationsberatung,
    },
    bottomStrip: {
      primary: { label: "Alles für Vorhabenträger", href: routes.content.vorhabentraeger },
      links: [{ label: "Preise vergleichen", href: routes.content.preisindex }],
    },
  },

  // -------------------------------------------------------------------------
  // 4. Für Flächeneigentümer — sellers (small dropdown).
  // -------------------------------------------------------------------------
  {
    id: "flaecheneigentuemer",
    triggerLabel: "Für Flächeneigentümer",
    layout: "dropdown",
    sections: [
      {
        id: "eigentuemer-liste",
        items: [
          {
            id: "oekopunkte-verkaufen",
            label: "Ökopunkte verkaufen",
            description: "Maßnahmen auf Ihrer Fläche als Ökopunkte vermarkten",
            href: routes.content.oekopunkteVerkaufen,
          },
          {
            id: "flaeche-anbieten",
            label: "Fläche anbieten",
            description: "Eine Ausgleichsfläche verkaufen oder bereitstellen",
            href: routes.content.flaecheAnbieten,
          },
          {
            id: "flaeche-bewerten",
            label: "Fläche kostenfrei bewerten lassen",
            description: "Kurzanalyse von LandVest, kostenfrei und unverbindlich",
            href: routes.content.flaecheBewertenLassen,
          },
          {
            id: "potenzialanalyse",
            label: "Potenzialanalyse Ihrer Fläche",
            description: "Ausführlicher Bericht von LandVest",
            href: routes.content.potenzialanalyseFlaeche,
          },
        ],
      },
    ],
    bottomStrip: {
      primary: { label: "Alles für Flächeneigentümer", href: routes.content.flaecheneigentuemer },
      links: [{ label: "Schnellangebot erstellen", href: routes.schnellangebot }],
    },
  },

  // -------------------------------------------------------------------------
  // 5. Wissen — four columns of plain article links.
  // -------------------------------------------------------------------------
  {
    id: "wissen",
    triggerLabel: "Wissen",
    sections: [
      // Column 1: Grundlagen (existing items)
      {
        id: "wissen-grundlagen",
        title: "Grundlagen",
        items: [
          {
            id: "preisindex",
            label: "Preisindex",
            href: routes.content.preisindex,
          },
          {
            id: "was-sind-oekopunkte",
            label: "Was sind Ökopunkte",
            href: routes.content.wasSindOekopunkte,
          },
          {
            id: "kompensationsarten",
            label: "Kompensationsarten im Überblick",
            href: routes.content.compensationTypes,
          },
          {
            id: "glossar",
            label: "Glossar",
            href: routes.content.glossar,
          },
          {
            id: "bundesland-uebersicht",
            label: "Bundesland-Übersicht",
            href: routes.content.bundeslandUebersicht,
          },
        ],
      },
      // Column 2: Naturraum & Kompensationsraum (cluster-pillar SEO pages)
      {
        id: "wissen-naturraum",
        title: "Naturraum & Kompensationsraum",
        items: [
          {
            id: "naturraum-plz",
            label: "Naturraum per PLZ bestimmen",
            href: routes.content.naturraumPlz,
          },
          {
            id: "bfn-naturraum",
            label: "BfN-Naturräume D01–D73",
            href: routes.content.bfnNaturraum,
          },
          {
            id: "kompensationsraum-vs-naturraum",
            label: "Kompensationsraum vs. Naturraum",
            href: routes.content.kompensationsraumVsNaturraum,
          },
          {
            id: "eingriffsregelung-vergleich",
            label: "Eingriffsregelung im Vergleich",
            href: routes.content.eingriffsregelungVergleich,
          },
        ],
      },
      // Column 3: Recht & Sicherung (legal hub + per-Bundesland spokes)
      {
        id: "wissen-recht-sicherung",
        title: "Recht & Sicherung",
        items: [
          {
            id: "dingliche-sicherung",
            label: "Dingliche Sicherung",
            href: routes.content.dinglicheSicherung,
          },
          {
            id: "dingliche-sicherung-niedersachsen",
            label: "Sicherung in Niedersachsen",
            href: routes.content.dinglicheSicherungIn("niedersachsen"),
          },
        ],
      },
      // Column 4: Erneuerbare Energien & Pacht (cluster hubs → /wissen anchors)
      {
        id: "wissen-praxis",
        title: "Erneuerbare Energien & Pacht",
        items: [
          {
            id: "solar-pv",
            label: "Photovoltaik",
            href: `${routes.content.wissen}#solar-pv`,
          },
          {
            id: "wind",
            label: "Windenergie",
            href: `${routes.content.wissen}#wind`,
          },
          {
            id: "pacht",
            label: "Pacht & Verträge",
            href: `${routes.content.wissen}#pacht`,
          },
          {
            id: "faqs-persona",
            label: "FAQs für Eigentümer",
            href: `${routes.content.wissen}#faqs`,
          },
        ],
      },
    ],
    bottomStrip: {
      primary: { label: "Alle Wissens-Themen", href: routes.content.wissen },
    },
  },
];

// Mobile navigation uses the same config
export const MOBILE_NAVIGATION_CONFIG = NAVIGATION_CONFIG;
